import { useEffect, useRef, useState } from "react";
import profileAvatar from "../../../assets/img/profile-avatar-gray.jpg";
import Card from "../../../components/Card/Card";
import List from "../../../components/List/List";
import Pagination from "../../../components/Pagination/Pagination";
import InvitationService from "../../../services/InvitationService";
import "./ListInvitation.css";
import { useAuth } from "../../../contexts/auth";
import { formatPeriod } from "../../../utils/date";
import { FaCheck, FaWhatsapp } from "react-icons/fa";
import { LuLink } from "react-icons/lu";
import { NotificationManager } from "react-notifications";
import { InvitationTypeIcons } from "../../../utils/invitationTypeIcons";
import { getInvitationTypeKey, InvitationTypeText } from "../../../enums/invitationTypes";
import { GoIssueDraft } from "react-icons/go";
import { FaX } from "react-icons/fa6";

const ListInvitation = (props) => {
	const [invitations, setInvitations] = useState([]);
	const [maxPage, setMaxPage] = useState(1);
	const [currentPage, setCurrentPage] = useState(1);
	const [collumnClicked, setCollumnClicked] = useState({
		collumnTitle: "createdAt",
		orderDirection: "DESC",
	});
	const { user } = useAuth();
	const [isLoading, setIsLoading] = useState(true);
	const cancelTokenRef = useRef(null);

	const getInvitations = async (
		page,
		searchText,
		organizationId,
		collumnClicked
	) => {
		if (organizationId === 0) return;
		if (cancelTokenRef.current) {
			cancelTokenRef.current.abort();
		}
		cancelTokenRef.current = new AbortController();
		setIsLoading(true);
		const response = await InvitationService.listInvitationsPaginated(
			page,
			searchText,
			organizationId,
			collumnClicked,
			cancelTokenRef.current.signal,
		);
		setIsLoading(false);
		setInvitations(response.data?.items || []);
		setMaxPage(response.data?.totalPages || 1);
	};

	useEffect(() => {
		getInvitations(
			currentPage,
			props.filters,
			user.userData.selectedOrganizationId,
			collumnClicked
		);
	}, [
		user.userData.selectedOrganizationId,
		props.filters,
		collumnClicked,
		props.refreshKey,
		currentPage
	]);

	return props.smallList ? (
		<List
			isLoading={isLoading}
			handleCollumnTitleClick={(collumnIndex) =>
				setCollumnClicked(collumnIndex)
			}
			collumsObjIndex={[{ collumnLabel: "Nome", index: "name", type: "text" }]}
			data={invitations.slice(0, 3)}
			listEntityName={"invitations"}
		/>
	) : (
		<>
			<div className="list-container list-invitations list-visibility">
				<List
					isLoading={isLoading}
					handleCollumnTitleClick={(collumnIndex) =>
						setCollumnClicked(collumnIndex)
					}
					collumsObjIndex={[
						{ collumnLabel: "Convidado", index: "name", type: "text" },
						{
							collumnLabel: "Tipo", type: "custom", custom: ({ row }) => {
								const IconComponent = InvitationTypeIcons[getInvitationTypeKey(row.type)] || null;
								return (
									<div className="inline-flex flex-row items-center gap-2">
										<IconComponent className="text-lg!" />
										<span className="font-bold!">{InvitationTypeText[row.type] || 'Desconhecido'}</span>
									</div>
								);
							}
						},
						{
							collumnLabel: "Contato", type: "custom", custom: ({ row }) => {
								const contact = row.phone && row.phone.trim() !== "" ? row.phone : row.mail;
								if (!contact) {
									return <span className="opacity-40">Sem contato</span>
								}
								return <a href={`${contact?.includes('@') ? 'mailto' : 'tel'}:${contact}`} className='hover:text-(--primaryColor) duration-200!'>{contact}</a>
							}
						},
						{
							collumnLabel: "Período",
							type: "custom",
							custom: ({ row }) => {
								const inicio = row.startDate;
								const fim = row.endDate;
								if (!inicio || !fim) return "Sem período";
								return formatPeriod(inicio, fim);
							}
						},
						{
							collumnLabel: "Status",
							forceLabel: true,
							index: "status",
							type: "status",
							custom: ({ row }) => {
								if (row.status === 'Open') return <span className="text-yellow-500 inline-flex items-center gap-2!"><GoIssueDraft />Em aberto</span>;
								if (row.status === 'Accepted') return <span className="text-green-400 inline-flex items-center gap-2!"><FaCheck />Aceito</span>;
								if (row.status === 'Finished') return <span className="text-green-400 inline-flex items-center gap-2!"><FaCheck />Finalizado</span>;
								if (row.status === 'Canceled') return <span className="text-red-500 inline-flex items-center gap-2!"><FaX />Cancelado</span>;
								if (row.status === 'Denied') return <span className="text-red-500 inline-flex items-center gap-2!"><FaX />Recusado</span>;
							}
						},
						{
							collumnLabel: "Ações", type: "actions", custom: ({ row }) => {
								const link = `${window.location.origin}/invitation/token/${row.token}`;
								return (
									<div className="flex justify-end gap-4">
										<button title="Copiar link" type="button" className={`cursor-pointer hover:opacity-70 duration-100`} onClick={async e => {
											e.stopPropagation();
											await navigator.clipboard.writeText(link);
											NotificationManager.success("Link copiado para a área de transferência", "Sucesso", 5000);
										}}>
											<LuLink size={18} />
										</button>
										{row.phone && (
											<button title="Enviar para o whatsapp" type="button" className={`cursor-pointer hover:opacity-70 duration-100`} onClick={async e => {
												e.stopPropagation();
												window.open(`https://wa.me/${row.phone.replaceAll(/\D/g, '')}?text=Olá! Você foi convidado para acessar o sistema. Acesse o link: ${link}`);
											}}>
												<FaWhatsapp size={18} />
											</button>
										)
										}
									</div>
								);
							}
						},
					]}
					data={invitations}
					listEntityName={"invitations"}
				/>
			</div >
			<div className="card-visibility">
				{invitations &&
					invitations.map((invitation, index) => (
						<Card
							key={index}
							data={invitation}
							image={profileAvatar}
							delete={() => InvitationService.deleteInvitation(invitation.id)}
							navigateOnDelete={"../consulta-invitations"}
							navigateOnEdit={"../cadastro-invitations"}
						>
							<p className="card-content-title">{invitation.name}</p>
							<p className="card-content">E-mail: {invitation.mail}</p>
							<p className="card-content">Telefone: {invitation.phone_number}</p>
							<p className="card-content">Unidade: {invitation.unit?.name || invitation.unitName || "Sem unidade"}</p>
						</Card>
					))}
			</div>
			<Pagination
				rowsPerPage={8}
				maxPage={maxPage}
				changePage={(page) =>
					setCurrentPage(page)
				}
			/>
		</>
	);
};

export default ListInvitation;
